import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, DatePicker, Space, Table, Tag, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { API } from '@/services/api.service';
import HowToCard from '../HowToCard';
import { errorText, formatAmount, saveBlob } from '../format';

const { Text } = Typography;

const formatDay = (date) => (date ? dayjs(date).format('DD.MM.YYYY') : '');

/**
 * The month's tax receivable tracking table (Berkosan "TAX RECEIVABLE
 * TRACKING TABLE"): every import's VAT paid to customs, i.e. the 274 debit of
 * the UV orders, with the total the company is owed. The download is the
 * accountant's Excel layout.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company whose ledger is used
 * @returns {JSX.Element} The panel
 */
export default function TaxReceivablePanel({ companyId }) {
  const { t } = useTranslation('clientReports');
  // The report goes out after the month closes, so last month is the default.
  const [month0, setMonth0] = useState(dayjs().subtract(1, 'month').startOf('month'));
  const year = month0.year();
  const month = month0.month() + 1;

  const { data, isLoading, error } = useQuery({
    queryKey: ['client-reports-tax-receivable', companyId, year, month],
    queryFn: () => API.clientReports.getTaxReceivable(companyId, { year, month }),
    retry: false,
  });
  const report = data?.data;
  // Present only when the company has customs declarations from SEF.
  const sefData = report?.sef;
  const hasSef = !!sefData;

  const exportMutation = useMutation({
    mutationFn: () => API.clientReports.exportTaxReceivable(companyId, { year, month }),
    onSuccess: saveBlob,
    onError: async (e) => message.error(await errorText(e)),
  });

  const columns = useMemo(
    () => [
      {
        title: t('taxReceivable.importDate'),
        dataIndex: 'importDate',
        key: 'importDate',
        width: 120,
        render: formatDay,
      },
      {
        title: t('taxReceivable.order'),
        dataIndex: 'orderNo',
        key: 'orderNo',
        width: 90,
      },
      {
        title: t('taxReceivable.explanation'),
        dataIndex: 'explanation',
        key: 'explanation',
      },
      {
        title: t('taxReceivable.paidToCustoms'),
        dataIndex: 'paidToCustoms',
        key: 'paidToCustoms',
        align: 'right',
        width: 170,
        render: formatAmount,
      },
      {
        title: t('taxReceivable.paymentDate'),
        dataIndex: 'paymentDate',
        key: 'paymentDate',
        width: 130,
        render: (date) => (date ? formatDay(date) : <Text type="secondary">{t('taxReceivable.notPaid')}</Text>),
      },
      {
        title: t('taxReceivable.deducted'),
        dataIndex: 'deducted',
        key: 'deducted',
        align: 'right',
        width: 170,
        render: formatAmount,
      },
      ...(hasSef ? sefColumns(t) : []),
    ],
    [t, hasSef]
  );

  // 404 = nothing has arrived from AB for that year yet.
  const errorMessage =
    error?.response?.status === 404
      ? t('fccs.noLedger', { year })
      : error?.response?.data?.message || error?.message;

  const rows = report?.rows || [];

  return (
    <div>
      <HowToCard guide="taxReceivable" open={report ? false : undefined} />
      <Space id="howto-taxReceivable-toolbar" wrap style={{ display: 'flex', marginBottom: 12 }}>
        <DatePicker
          picker="month"
          allowClear={false}
          value={month0}
          format="MM.YYYY"
          onChange={(value) => value && setMonth0(value.startOf('month'))}
        />
        <Button
          type="primary"
          shape="round"
          className="btn-primary"
          icon={<DownloadOutlined />}
          disabled={!report}
          loading={exportMutation.isPending}
          onClick={() => exportMutation.mutate()}
        >
          {t('taxReceivable.download')}
        </Button>
      </Space>

      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>{t('taxReceivable.hint')}</div>

      {errorMessage && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={errorMessage} />
      )}

      {report && (
        <Table
          scroll={{ x: 'max-content' }}
          rowKey={(r) => `${r.orderNo}|${r.importDate}`}
          size="small"
          pagination={false}
          loading={isLoading}
          columns={columns}
          dataSource={rows}
          locale={{ emptyText: t('taxReceivable.empty') }}
          summary={() =>
            rows.length > 0 && (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <Text strong>Σ</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <Text strong>{formatAmount(report.totals.paidToCustoms)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} />
                  <Table.Summary.Cell index={5} align="right">
                    <Text strong>{formatAmount(report.totals.deducted)}</Text>
                  </Table.Summary.Cell>
                  {hasSef && (
                    <>
                      <Table.Summary.Cell index={6} />
                      <Table.Summary.Cell index={7} align="right">
                        <Text strong>{formatAmount(sumBy(rows, (r) => r.sef?.vat))}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={8} colSpan={2} />
                    </>
                  )}
                </Table.Summary.Row>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <Text strong>{t('taxReceivable.receivable')}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <Text strong>{formatAmount(report.totals.receivable)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} colSpan={hasSef ? 6 : 2} />
                </Table.Summary.Row>
              </Table.Summary>
            )
          }
        />
      )}

      {report && hasSef && (
        <div style={{ marginTop: 8, fontSize: 13 }}>
          <Text strong>{t('taxReceivable.sef.monthVat', { amount: formatAmount(sefData.sefVat) })}</Text>
          <Text type="secondary">
            {' · '}
            {t('taxReceivable.sef.booksTotal', { amount: formatAmount(report.totals?.paidToCustoms) })}
          </Text>
        </div>
      )}

      {report && sefData?.sefOnly?.length > 0 && (
        <Card size="small" style={{ marginTop: 12 }} title={t('taxReceivable.sef.sefOnlyTitle')}>
          <Table
            size="small"
            pagination={false}
            scroll={{ x: 'max-content' }}
            rowKey={(r) => r.declarationNumber}
            dataSource={sefData.sefOnly}
            columns={[
              {
                title: t('taxReceivable.sef.jci'),
                key: 'jci',
                render: (_, r) => <JciText sef={r} />,
              },
              {
                title: t('taxReceivable.sef.date'),
                dataIndex: 'customsDate',
                key: 'customsDate',
                width: 120,
                render: formatDay,
              },
              {
                title: t('taxReceivable.sef.vat'),
                dataIndex: 'vat',
                key: 'vat',
                align: 'right',
                render: formatAmount,
              },
              {
                title: t('taxReceivable.sef.paidRemaining'),
                key: 'paid',
                align: 'right',
                render: (_, r) => <PaidRemaining sef={r} t={t} />,
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}

const sumBy = (list, pick) => list.reduce((acc, item) => acc + (Number(pick(item)) || 0), 0);

// Declaration number, customs invoice number under it.
const JciText = ({ sef }) => (
  <div>
    <div>{sef.declarationNumber}</div>
    {sef.customsInvoiceNumber && (
      <Text type="secondary" style={{ fontSize: 12 }}>
        {sef.customsInvoiceNumber}
      </Text>
    )}
  </div>
);

const PaidRemaining = ({ sef, t }) => (
  <Space size={4}>
    <span>{formatAmount(sef.vatPaid)}</span>
    <span>/</span>
    {sef.remainingVat > 0 ? (
      <Tag color="red" style={{ marginInlineEnd: 0 }} title={t('taxReceivable.sef.unpaid')}>
        {formatAmount(sef.remainingVat)}
      </Tag>
    ) : (
      <span>{formatAmount(sef.remainingVat)}</span>
    )}
  </Space>
);

// Extra columns when the company has customs declarations from SEF.
const sefColumns = (t) => [
  {
    title: t('taxReceivable.sef.jci'),
    key: 'sefJci',
    width: 170,
    render: (_, r) => (r.sef ? <JciText sef={r.sef} /> : null),
  },
  {
    title: t('taxReceivable.sef.vat'),
    key: 'sefVat',
    align: 'right',
    width: 150,
    render: (_, r) => (r.sef ? formatAmount(r.sef.vat) : null),
  },
  {
    title: t('taxReceivable.sef.paidRemaining'),
    key: 'sefPaid',
    align: 'right',
    width: 200,
    render: (_, r) => (r.sef ? <PaidRemaining sef={r.sef} t={t} /> : null),
  },
  {
    title: t('taxReceivable.sef.difference'),
    key: 'sefDifference',
    align: 'center',
    width: 140,
    render: (_, r) => {
      if (!r.sef) return <Tag color="default">{t('taxReceivable.sef.notOnSef')}</Tag>;
      if (r.sefDifference === 0) {
        return <CheckCircleOutlined style={{ color: '#52c41a' }} title={t('taxReceivable.sef.match')} />;
      }
      return <Tag color="orange">{formatAmount(r.sefDifference)}</Tag>;
    },
  },
];
